import {
  Flex,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  HStack,
  DrawerHeader,
  useDisclosure,
  IconButton,
  VStack,
  Skeleton,
  Box,
  useColorModeValue,
  Modal,
  ModalBody,
  ModalCloseButton,
  CloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Alert,
  AlertDescription,
  AlertIcon,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import React, { useState } from 'react';

import { url as commonUrl } from '@finley-technologies/common';

import {
  useGetCreditFacilityQuery,
  AssignmentConfirmationJobGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  LoanProductCreditFacilityReceivableUploadMap,
  initialUploadState,
  ReceivableUpload,
} from '../AssignmentEditByCSVContainer';
import { UploadReceivablesFileForm } from './UploadReceivablesFileForm';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';

const ActionsDrawerShell = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Drawer onClose={onClose} isOpen size="xl" closeOnOverlayClick>
      <DrawerOverlay />
      <DrawerContent p={0}>{children}</DrawerContent>
    </Drawer>
  );
};

const ActionsDrawerHeader = ({
  onClose,
  actionsDrawerState,
}: {
  onClose: () => void;
  actionsDrawerState: ActionsDrawerState;
}) => {
  const headerText =
    actionsDrawerState === 'repurchase' ? 'Repurchase by CSV' : 'Transfer';
  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      w="100%"
      p="40px 40px 24px"
      borderBottom="1px solid"
      borderBottomColor="gray.100"
    >
      <DrawerHeader flexGrow={1} maxWidth="90%" p="0px">
        {headerText}
      </DrawerHeader>
      <IconButton
        aria-label="Close"
        icon={<FaIcon icon="xmark-large" />}
        onClick={onClose}
        size="sm"
        variant="v2-ghost"
      />
    </HStack>
  );
};

export const UploadFileModal = ({
  isOpen,
  onClose,
  children,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent padding="20px" width="auto" minW="550px">
        <ModalHeader>Upload File</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>
      </ModalContent>
    </Modal>
  );
};

const UploadState = ({
  receivableUpload,
  openUploadModal,
  kickingOffRepurchase,
}: {
  receivableUpload: ReceivableUpload;
  openUploadModal: () => void;
  kickingOffRepurchase: boolean;
}) => {
  const uploadColor = useColorModeValue(colors.blue[400], colors.white[700]);
  const successColor = useColorModeValue(colors.green[400], colors.green[300]);
  const errorColor = useColorModeValue(colors.red[400], colors.red[300]);

  if (!receivableUpload.file) {
    return (
      <Button
        size="md"
        colorScheme="v2-aurora"
        variant="secondary"
        onClick={openUploadModal}
        background="white"
        ml="12px"
        isDisabled={kickingOffRepurchase}
      >
        Upload
      </Button>
    );
  }
  if (receivableUpload.isLoading) {
    return (
      <Flex alignItems="center">
        <Spinner color={uploadColor} />
        <Button
          size="md"
          colorScheme="v2-aurora"
          variant="secondary"
          background="white"
          ml="12px"
          isDisabled
        >
          Upload
        </Button>
      </Flex>
    );
  }
  if (receivableUpload.isValid) {
    return (
      <Flex alignItems="center">
        <FaIcon color={successColor} icon="check-circle" size="16px" />
        <Typography
          variant="base-muted"
          TextProps={{ fontSize: '14px', ml: '12px' }}
        >
          Uploaded
        </Typography>
      </Flex>
    );
  }
  return (
    <Flex alignItems="center">
      <FaIcon color={errorColor} icon="circle-xmark" size="16px" />
      <Button
        size="md"
        colorScheme="v2-aurora"
        variant="secondary"
        onClick={openUploadModal}
        background="white"
        ml="12px"
        isDisabled={kickingOffRepurchase}
      >
        Re-upload
      </Button>
    </Flex>
  );
};

export const UploadFileInput = ({
  receivableUpload,
  validateFileStructure,
  setReceivableUploadsMap,
  kickingOffRepurchase,
}: {
  receivableUpload: ReceivableUpload;
  validateFileStructure: (
    file: File,
    receivableUpload: ReceivableUpload,
  ) => Promise<void>;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  kickingOffRepurchase: boolean;
}) => {
  const uploadFileModal = useDisclosure();

  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');

  return (
    <>
      <VStack
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${borderColor}`}
        bg={backgroundColor}
        width="100%"
        mb={receivableUpload.isValid ? '24px' : '16px'}
      >
        <HStack width="100%" justifyContent="space-between">
          <Typography TextProps={{ fontWeight: 500 }}>
            {receivableUpload.loanProductName}
          </Typography>
          <UploadState
            receivableUpload={receivableUpload}
            openUploadModal={() => uploadFileModal.onOpen()}
            kickingOffRepurchase={kickingOffRepurchase}
          />
          <UploadFileModal
            isOpen={uploadFileModal.isOpen}
            onClose={uploadFileModal.onClose}
          >
            <UploadReceivablesFileForm
              onClose={uploadFileModal.onClose}
              handleFile={(file) => {
                validateFileStructure(file, receivableUpload);
              }}
            />
          </UploadFileModal>
        </HStack>
        <Flex
          mt="10px"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Typography variant="base-muted" TextProps={{ fontSize: '14px' }}>
            {receivableUpload.creditFacilityName}
          </Typography>
          {receivableUpload.file && (
            <Flex alignItems="center">
              <Typography
                variant="base-muted"
                TextProps={{ fontSize: '14px', mr: '8px' }}
              >
                {receivableUpload.file?.name}
              </Typography>
              <CloseButton
                onClick={() => {
                  setReceivableUploadsMap((receivableUploadsMap) => {
                    return {
                      ...receivableUploadsMap,
                      [receivableUpload.key]: {
                        ...receivableUploadsMap[receivableUpload.key],
                        isLoading: false,
                        ...initialUploadState,
                      },
                    };
                  });
                }}
                colorScheme="gray"
                isDisabled={kickingOffRepurchase}
              />
            </Flex>
          )}
        </Flex>
      </VStack>
      {receivableUpload.fileError && (
        <Alert status="error" mb="24px">
          <AlertIcon />
          <AlertDescription>
            <Typography TextProps={{ fontWeight: 500 }}>
              Upload a valid CSV file with no header and a single column
              containing only the Receivable IDs to proceed.
            </Typography>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};

export const RepurchaseDrawer = ({
  onClose,
  receivableUploadsMap,
  setReceivableUploadsMap,
  creditFacilityId,
  setInitiatedJobInfo,
}: {
  onClose: () => void;
  receivableUploadsMap: LoanProductCreditFacilityReceivableUploadMap;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  creditFacilityId: string;
  setInitiatedJobInfo: React.Dispatch<
    React.SetStateAction<{
      id: string;
      jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
      receivableCount: number;
    } | null>
  >;
}) => {
  const toast = useToast();
  const { accessToken } = useUserAuthOrThrow();

  const [kickingOffRepurchase, setKickingOffRepurchase] = useState<boolean>(
    false,
  );

  // Perform basic file structure validation on each file addition, checking that the file is single column.
  const validateFileStructure = async (
    file: File,
    receivableUpload: ReceivableUpload,
  ): Promise<void> => {
    // Reset error state and display loading icon
    setReceivableUploadsMap({
      ...receivableUploadsMap,
      [receivableUpload.key]: {
        ...receivableUploadsMap[receivableUpload.key],
        isLoading: true,
        ...initialUploadState,
      },
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignmentCSVValidation,
    );
    const formData = new FormData();
    formData.append('loanProductId', receivableUpload.loanProductId);
    formData.append('creditFacilityId', creditFacilityId);
    formData.append('receivables', file);
    formData.append('assignmentType', 'repurchaseByCSV');

    try {
      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });

      const fileError = data.isValid ? '' : data.fileErrors[0];

      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        isValid: data.isValid,
        isLoading: false,
        fileError,
        file,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing file. Please try again, and contact your Finley representative if this persists.',
      });
      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        // Stop the spinner.
        isLoading: false,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    }
  };

  const initiateRepurchaseConfirmationJob = async (): Promise<void> => {
    const formData = new FormData();

    const loanProductIds: string[] = [];

    Object.values(receivableUploadsMap).forEach((upload) => {
      if (upload.isValid && upload.file) {
        loanProductIds.push(upload.loanProductId);
        formData.append(`${upload.loanProductId}-file`, upload.file);
      }
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.repurchaseByCSV,
    );
    formData.append('creditFacilityId', creditFacilityId);
    formData.append('loanProductIds', JSON.stringify(loanProductIds));

    try {
      const { data } = await axios.post(url, formData, {
        timeout: 300000, // 5 minute timeout
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });
      setInitiatedJobInfo({
        id: data.repurchaseConfirmationJobId,
        jobType: 'repurchase',
        receivableCount: data.receivableRepurchaseCount,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error initiating receivable repurchase. Please try again, and contact your Finley representative if this persists.',
      });
    }
  };

  return (
    <ActionsDrawerShell onClose={onClose}>
      <ActionsDrawerHeader onClose={onClose} actionsDrawerState="repurchase" />
      <Flex p="40px" direction="column" justifyContent="space-between" h="100%">
        <Flex direction="column">
          <Typography variant="base-muted">
            Upload a header-less CSV file with a single column containing the
            Receivable IDs you would like to repurchase and move to the balance
            sheet.
          </Typography>
          <Flex mt="44px" direction="column" gap="24px">
            <Typography TextProps={{ fontSize: '16px', fontWeight: 600 }}>
              Upload Files
            </Typography>
            <VStack spacing="8px" alignItems="flex-start" width="100%">
              {Object.values(receivableUploadsMap).map((receivableUpload) => {
                return (
                  <UploadFileInput
                    key={receivableUpload.key}
                    validateFileStructure={validateFileStructure}
                    receivableUpload={receivableUpload}
                    setReceivableUploadsMap={setReceivableUploadsMap}
                    kickingOffRepurchase={kickingOffRepurchase}
                  />
                );
              })}
            </VStack>
          </Flex>
        </Flex>
        <Flex justifyContent="space-between">
          <Button
            colorScheme="v2-aurora"
            variant="secondary"
            onClick={() => onClose()}
            isDisabled={kickingOffRepurchase}
          >
            Back
          </Button>
          <Button
            colorScheme="v2-aurora"
            onClick={async () => {
              setKickingOffRepurchase(true);
              await initiateRepurchaseConfirmationJob();
              setKickingOffRepurchase(false);
              onClose();
            }}
            isDisabled={kickingOffRepurchase}
          >
            Submit
          </Button>
        </Flex>
      </Flex>
    </ActionsDrawerShell>
  );
};

export type ActionsDrawerState = 'repurchase' | 'transfer';
export const ActionsDrawer = ({
  actionsDrawerState,
  setActionsDrawerState,
  creditFacilityId,
  setInitiatedJobInfo,
}: {
  actionsDrawerState: ActionsDrawerState;
  setActionsDrawerState: React.Dispatch<
    React.SetStateAction<ActionsDrawerState | null>
  >;
  creditFacilityId: string;
  setInitiatedJobInfo: React.Dispatch<
    React.SetStateAction<{
      id: string;
      jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
      receivableCount: number;
    } | null>
  >;
}) => {
  const [
    receivableUploadsMap,
    setReceivableUploadsMap,
  ] = useState<LoanProductCreditFacilityReceivableUploadMap>({});

  const {
    data: creditFacilityData,
    loading: creditFacilityLoading,
    error: creditFacilityError,
  } = useGetCreditFacilityQuery({
    variables: { creditFacilityId },
    onCompleted(data) {
      const receivableUploadMap: LoanProductCreditFacilityReceivableUploadMap = {};

      const { name: creditFacilityName, loanProducts } = data.creditFacility;

      loanProducts.forEach((loanProduct) => {
        receivableUploadMap[loanProduct.id + creditFacilityId] = {
          loanProductId: loanProduct.id,
          loanProductName: loanProduct.name,
          creditFacilityId,
          creditFacilityName,
          key: loanProduct.id + creditFacilityId,
          isLoading: false,
          ...initialUploadState,
        };
      });

      setReceivableUploadsMap(receivableUploadMap);
    },
    fetchPolicy: 'network-only',
  });

  if (creditFacilityLoading || !creditFacilityData || creditFacilityError) {
    return (
      <ActionsDrawerShell onClose={() => setActionsDrawerState(null)}>
        <ActionsDrawerHeader
          onClose={() => setActionsDrawerState(null)}
          actionsDrawerState={actionsDrawerState}
        />
        <Box p="40px">
          {creditFacilityLoading && <Skeleton height="600px" width="100%" />}
          {creditFacilityError && (
            <Alert status="error">
              <AlertIcon />
              Failed to load credit facility data.
            </Alert>
          )}
        </Box>
      </ActionsDrawerShell>
    );
  }

  return (
    <>
      {actionsDrawerState === 'repurchase' && (
        <RepurchaseDrawer
          onClose={() => setActionsDrawerState(null)}
          receivableUploadsMap={receivableUploadsMap}
          setReceivableUploadsMap={setReceivableUploadsMap}
          creditFacilityId={creditFacilityData.creditFacility.id}
          setInitiatedJobInfo={setInitiatedJobInfo}
        />
      )}
    </>
  );
};
